/**
 * A region's sketch (docs/archive/OPEN_WORLD.md §4.1): vector shapes in `data/regions/<id>.ts`, compiled
 * once into features bucketed per 64 m chunk, and evaluated on the 4 m sketch cells. Ported from
 * `scripts/layouts/open-world/region-b.mjs` (`buildRegion`, the plan images' reference raster).
 *
 * Pure: no DOM, timers or Math.random. The spires are scattered with a seeded mulberry32 at compile
 * time; everything else hashes coordinates. A cell's value depends only on its coordinates.
 */
import { mulberry32 } from '../rng';
import type { Facing } from '../map';
import type { Side, ZoneId } from '../data/zones';
import { CHUNK } from './chunk';

export type XY = readonly [number, number];

/** Sketch ground kinds (the plan's legend), as region-b.mjs's `K`. */
export const RK = {
  SEA: 0,
  SAND: 1,
  GRASS: 2,
  FOREST: 3,
  PINE: 4,
  BAMBOO: 5,
  FARM: 6,
  TEA: 7,
  FLOWER: 8,
  BLOSSOM: 9,
  ROCK: 10,
  CLIFF: 11,
  SPIRE: 12,
  RIVER: 13,
  LAKE: 14,
  ROAD: 15,
  TOWN: 16,
  BUILDING: 17,
  DECK: 18,
  REDROCK: 19,
  MARSH: 20,
  RUIN: 21,
  CAMP: 22,
  /** Not in the sketch: a blockout box (generate.ts). */
  BOX: 23,
} as const;
export type RegionKind = (typeof RK)[keyof typeof RK];

export type CoverName = 'bamboo' | 'pine' | 'farm' | 'tea' | 'flower' | 'blossom' | 'forest' | 'redrock';
const COVER_KIND: Readonly<Record<CoverName, RegionKind>> = {
  bamboo: RK.BAMBOO,
  pine: RK.PINE,
  farm: RK.FARM,
  tea: RK.TEA,
  flower: RK.FLOWER,
  blossom: RK.BLOSSOM,
  forest: RK.FOREST,
  redrock: RK.REDROCK,
};

export interface RegionSketch {
  readonly id: string;
  /**
   * The map is mirrored north-south (region/flip.ts, the แคว้นเก้ากระบี่): the sea lies SOUTH of the coast line, the
   * southern range is the northern one (`southRidge.edgeFrom` is a limit it rises toward from the north). Absent: sea north.
   */
  readonly flipped?: boolean;
  /** Side in metres (tiles). */
  readonly size: number;
  readonly seed: number;
  /** The authored layout stamped unchanged with its tile (0, 0) at (x, y). */
  readonly core: { readonly x: number; readonly y: number; readonly layout: string };
  readonly coast: readonly XY[];
  readonly islands: readonly { x: number; y: number; r: number; h: number; name: string }[];
  readonly stoneLegs: readonly (readonly [XY, XY])[];
  readonly peaks: readonly { x: number; y: number; h: number; r: number }[];
  readonly southRidge: { readonly pts: readonly XY[]; readonly h: number; readonly reach: number; readonly edgeFrom: number };
  readonly passes: readonly { id: string; x: number; y: number; r: number; name: string; to: ZoneId }[];
  /** Ground levelled to `h` (± `noise` metres, default 2). */
  readonly flats: readonly { x: number; y: number; r: number; h: number; name: string; noise?: number }[];
  readonly rivers: readonly { name: string; pts: readonly XY[]; bed: readonly number[]; w: readonly number[] }[];
  readonly falls: readonly { x: number; y: number; drop: number; name: string }[];
  readonly lakes: readonly { x: number; y: number; rx: number; ry: number; h: number; name: string; marsh?: boolean }[];
  readonly cover: readonly (readonly [CoverName, number, number, number])[];
  readonly roads: readonly { id: string; name: string; main?: boolean; pts: readonly XY[] }[];
  readonly grounds: readonly { x: number; y: number; rw: number; rh: number; round?: boolean; cover: 'town' | 'camp' | 'ruin' }[];
  readonly spires: {
    readonly box: readonly [x: number, y: number, w: number, h: number];
    readonly n: number;
    readonly seed: number;
    readonly floor: number;
    readonly avoidRoad: string;
    /** Circles [x, y, r] kept clear of spires (steps, villages, grounds next to the box). */
    readonly avoid?: readonly (readonly [number, number, number])[];
    readonly extra: readonly { x: number; y: number; r: number; top: number }[];
  };
  /** The highest ground anywhere (the golden summit). */
  readonly maxHeight?: number;
  /**
   * Metres per design unit of the sketch's own constants (noise scales, the coastal plain, banks, spires, the cover
   * thresholds): a region drawn at a fraction of its design (the 1 km จู๋หมิง, data/regions/zhuming.ts, at 1/3) sets it
   * so its shapes keep the design's look. Its data is already in metres. Left out: 1 (every constant as written).
   */
  readonly unit?: number;
  /**
   * The playable outline (docs/archive/HUB.md §5): a closed polygon. The chunk table is its bounding square;
   * mainland outside it rises into cliffs and peaks (`OUTLINE_RIM`), drawn only as far terrain. The
   * sea and the islands are left as they are. Passes cut through the rim.
   */
  readonly outline?: readonly XY[];
  /** Extra named points of the region's map, in region metres (the shut gates of a starter region): `GameMap.landmarks`. */
  readonly landmarks?: Readonly<Record<string, XY>>;
  readonly stations: readonly { id: string; name: string; x: number; y: number }[];
  readonly portals: readonly { to: ZoneId; side: Side; x: number; y: number; name: string }[];
  /** The open country's hunting grounds (systems/activity.ts populates them when an active cell first touches them). */
  readonly wilds?: readonly RegionWild[];
}

/**
 * A wild spawn region of the open country: a rectangle of region metres and the monsters
 * (`data/monsters.ts` ids) that live in it, set down when a player comes near.
 */
export interface RegionWild {
  readonly id: string;
  /** The place number of docs/archive/OPEN_WORLD.md §2.2. */
  readonly place: number;
  readonly name: string;
  /** [x0, x1, y0, y1] in region metres (like a map's `regions`). */
  readonly rect: readonly [number, number, number, number];
  readonly spawns: readonly { readonly monster: string; readonly n: number }[];
}

/** One greybox mass (data/regions/<id>.blockout.ts): a solid box with a standable top. */
export interface BlockoutBox {
  /** Stable id: detail lanes replace boxes by id. */
  readonly id: string;
  /** The place number of docs/archive/OPEN_WORLD.md §2.2. */
  readonly place: number;
  /** Thai name (from the doc). */
  readonly name: string;
  /** Centre and footprint (metres, x along east, d along south). */
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly d: number;
  /** Height over its base (metres); negative for a pit. A ramp rises `h` toward its top side. */
  readonly h: number;
  /** Base level (metres above the sea); default the ground at the centre, or the top of `on`. */
  readonly base?: number;
  /** Stands on another box: its base is that box's top. */
  readonly on?: string;
  readonly kind:
    | 'wall'
    | 'gate'
    | 'hall'
    | 'house'
    | 'tower'
    | 'tier'
    | 'terrace'
    | 'ramp'
    | 'landing'
    | 'spire'
    | 'stone'
    | 'camp'
    | 'tent'
    | 'pier'
    | 'ledge'
    | 'pit'
    | 'station'
    | 'ruin'
    | 'mound';
  /** Ramps: the side the low end is on. */
  readonly down?: Facing;
  /** Which detail lane should take it (docs/archive/REGION_GREYBOX.md). */
  readonly lane: string;
  readonly note?: string;
}

export interface Spire {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly r: number;
  readonly top: number;
  readonly pagoda: boolean;
}

// ── noise and geometry ───────────────────────────────────────────────────────────────────────

export const hash3 = (i: number, j: number, s = 0): number => {
  let h = (Math.imul(i | 0, 374761393) + Math.imul(j | 0, 668265263) + Math.imul(s | 0, 1442695041)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
};
export const smooth = (t: number): number => (t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t));
export function vnoise(x: number, y: number, s = 0): number {
  const i = Math.floor(x);
  const j = Math.floor(y);
  const fx = smooth(x - i);
  const fy = smooth(y - j);
  const a = hash3(i, j, s);
  const b = hash3(i + 1, j, s);
  const c = hash3(i, j + 1, s);
  const d = hash3(i + 1, j + 1, s);
  return a + (b - a) * fx + (c - a) * fy + (a - b - c + d) * fx * fy;
}
export function fbm(x: number, y: number, s = 0, oct = 4): number {
  let v = 0;
  let a = 0.5;
  let f = 1;
  for (let o = 0; o < oct; o++) {
    v += a * vnoise(x * f, y * f, s + o * 17);
    f *= 2;
    a *= 0.5;
  }
  return v / (1 - 0.5 ** oct);
}
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

/** A polyline with its segment lengths and the length before each segment. */
export interface Poly {
  readonly pts: readonly XY[];
  readonly lens: readonly number[];
  readonly acc: readonly number[];
  readonly total: number;
}
export function poly(pts: readonly XY[]): Poly {
  const lens: number[] = [];
  const acc: number[] = [];
  let total = 0;
  for (let k = 1; k < pts.length; k++) {
    const l = Math.hypot(pts[k]![0] - pts[k - 1]![0], pts[k]![1] - pts[k - 1]![1]);
    acc.push(total);
    lens.push(l);
    total += l;
  }
  return { pts, lens, acc, total };
}
/** A polyline cut to the segments near a chunk (indices of segments; segment k runs pts[k]→pts[k+1]). */
export interface PolyPart {
  readonly p: Poly;
  readonly segs: readonly number[];
}
const ALL_SEGS = new WeakMap<Poly, number[]>();
/**
 * Distance from (x, y) to the polyline (or to its segments `segs`), the fraction along the whole
 * line of the nearest point, and that point.
 */
export function polyNear(p: Poly, x: number, y: number, segs?: readonly number[]): { d: number; t: number; px: number; py: number } {
  let list = segs;
  if (!list) {
    let all = ALL_SEGS.get(p);
    if (!all) {
      all = p.lens.map((_, k) => k);
      ALL_SEGS.set(p, all);
    }
    list = all;
  }
  let best = Infinity;
  let bestT = 0;
  let px = 0;
  let py = 0;
  for (const k of list) {
    const [x0, y0] = p.pts[k]!;
    const [x1, y1] = p.pts[k + 1]!;
    const dx = x1 - x0;
    const dy = y1 - y0;
    const l2 = dx * dx + dy * dy || 1;
    const t = clamp(((x - x0) * dx + (y - y0) * dy) / l2, 0, 1);
    const qx = x0 + dx * t;
    const qy = y0 + dy * t;
    const d = Math.hypot(x - qx, y - qy);
    if (d < best) {
      best = d;
      bestT = (p.acc[k]! + p.lens[k]! * t) / (p.total || 1);
      px = qx;
      py = qy;
    }
  }
  return { d: best, t: bestT, px, py };
}
/** The segments of a polyline whose bounding boxes come within `reach` of a box. */
function segsNear(p: Poly, x0: number, y0: number, x1: number, y1: number, reach: number): number[] {
  const out: number[] = [];
  const pts = p.pts;
  for (let k = 0; k + 1 < pts.length; k++) {
    const ax = Math.min(pts[k]![0], pts[k + 1]![0]);
    const bx = Math.max(pts[k]![0], pts[k + 1]![0]);
    const ay = Math.min(pts[k]![1], pts[k + 1]![1]);
    const by = Math.max(pts[k]![1], pts[k + 1]![1]);
    const dx = Math.max(0, ax - x1, x0 - bx);
    const dy = Math.max(0, ay - y1, y0 - by);
    if (Math.hypot(dx, dy) < reach) out.push(k);
  }
  return out;
}

// ── compile ─────────────────────────────────────────────────────────────────────────────────

/** Metres round a chunk that its bucket also covers (the cell grid's margin and the road widths). */
export const BUCKET_MARGIN = 24;

interface Bucket {
  peaks: RegionSketch['peaks'][number][];
  islands: RegionSketch['islands'][number][];
  flats: RegionSketch['flats'][number][];
  lakes: RegionSketch['lakes'][number][];
  rivers: { r: RegionSketch['rivers'][number]; p: Poly; segs: number[] }[];
  cover: (readonly [RegionKind, number, number, number])[];
  grounds: RegionSketch['grounds'][number][];
  passes: RegionSketch['passes'][number][];
  roads: { id: string; main: boolean; p: Poly; segs: number[] }[];
  spires: Spire[];
  /** The south ridge's segments within its reach, or null when the chunk is beyond it. */
  ridge: number[] | null;
  /**
   * The outline's rim over this chunk: 0 when the chunk lies wholly inside, beyond the rim's reach
   * of the edge (no rim); 1 when it lies wholly outside past the reach (the full rim); 2 near the edge
   * (measured per cell).
   */
  rim: 0 | 1 | 2;
}

export interface CompiledSketch {
  readonly s: RegionSketch;
  readonly nc: number;
  readonly spires: readonly Spire[];
  /** Stepping stone centres across the bay. */
  readonly stones: readonly XY[];
  readonly ridge: Poly;
  /** The outline as a closed polyline, or null. */
  readonly outline: Poly | null;
  bucket(ci: number, cj: number): Bucket;
}

/** The rim outside the outline: metres it rises at the edge, at full reach, and the reach (m). */
export const OUTLINE_RIM = { edge: 60, top: 200, reach: 110 } as const;

/** Whether (x, y) lies inside the closed polygon `pts` (even-odd). */
export function insidePoly(pts: readonly XY[], x: number, y: number): boolean {
  let inside = false;
  for (let k = 0, l = pts.length - 1; k < pts.length; l = k++) {
    const [xk, yk] = pts[k]!;
    const [xl, yl] = pts[l]!;
    if (yk > y !== yl > y && x < ((xl - xk) * (y - yk)) / (yl - yk) + xk) inside = !inside;
  }
  return inside;
}

/** The stone forest's spires: seeded scatter, as region-b.mjs. */
function scatterSpires(s: RegionSketch, road: Poly | undefined): Spire[] {
  const sp = s.spires;
  const rnd = mulberry32(sp.seed);
  const u = s.unit ?? 1;
  const out: { x: number; y: number; r: number; top: number; pagoda: boolean }[] = [];
  // Bounded tries: a box too small for `n` spires gets fewer, never a hang.
  for (let tries = 0; out.length < sp.n && tries < 50_000; tries++) {
    const x = sp.box[0] + rnd() * sp.box[2];
    const y = sp.box[1] + rnd() * sp.box[3];
    const r = (9 + rnd() * 18) * u;
    if (out.some((q) => Math.hypot(q.x - x, q.y - y) < q.r + r + 22 * u)) continue;
    if (road && polyNear(road, x, y).d < r + 14 * u) continue;
    if (sp.avoid?.some(([ax, ay, ar]) => Math.hypot(ax - x, ay - y) < ar + r)) continue;
    out.push({ x, y, r, top: sp.floor + (45 + rnd() * 125) * u, pagoda: rnd() < 0.18 });
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return [
    ...out.map((q, k) => ({ id: `stoneforest.spire.${pad(k + 1)}`, ...q })),
    ...sp.extra.map((q, k) => ({ id: `bay.stack.${k + 1}`, ...q, pagoda: false })),
  ];
}

export function compileSketch(s: RegionSketch): CompiledSketch {
  const nc = Math.ceil(s.size / CHUNK);
  const roads = s.roads.map((r) => ({ id: r.id, main: !!r.main, p: poly(r.pts) }));
  const spires = scatterSpires(s, roads.find((r) => r.id === s.spires.avoidRoad)?.p);
  const stones: XY[] = [];
  for (const [[x0, y0], [x1, y1]] of s.stoneLegs) {
    const n = Math.max(1, Math.round(Math.hypot(x1 - x0, y1 - y0) / (50 * (s.unit ?? 1))));
    for (let k = 1; k < n; k++) stones.push([lerp(x0, x1, k / n), lerp(y0, y1, k / n)]);
  }
  const rivers = s.rivers.map((r) => ({ r, p: poly(r.pts) }));
  const ridge = poly(s.southRidge.pts);
  const outline = s.outline ? poly([...s.outline, s.outline[0]!]) : null;
  const cover = s.cover.map(([c, x, y, r]) => [COVER_KIND[c], x, y, r] as const);
  const buckets = new Map<number, Bucket>();
  const bucket = (ci: number, cj: number): Bucket => {
    const key = cj * nc + ci;
    let b = buckets.get(key);
    if (b) return b;
    const x0 = ci * CHUNK - BUCKET_MARGIN;
    const y0 = cj * CHUNK - BUCKET_MARGIN;
    const x1 = (ci + 1) * CHUNK + BUCKET_MARGIN;
    const y1 = (cj + 1) * CHUNK + BUCKET_MARGIN;
    const near = (x: number, y: number, r: number) =>
      Math.hypot(Math.max(0, x0 - x, x - x1), Math.max(0, y0 - y, y - y1)) < r;
    b = {
      peaks: s.peaks.filter((p) => near(p.x, p.y, p.r)),
      islands: s.islands.filter((p) => near(p.x, p.y, p.r * 1.15)),
      flats: s.flats.filter((p) => near(p.x, p.y, p.r * 1.4)),
      lakes: s.lakes.filter((p) => near(p.x, p.y, Math.max(p.rx, p.ry) * 1.6 / 0.9)),
      // A river shapes the ground only within 160 m of it: the segments beyond never win.
      rivers: rivers.map((r) => ({ ...r, segs: segsNear(r.p, x0, y0, x1, y1, 160) })).filter((r) => r.segs.length),
      // Cover zones are warped by up to 0.45 r and scaled by up to 1.2.
      cover: cover.filter(([, x, y, r]) => near(x, y, r * 1.2 + r * 0.45 * 1.5)),
      grounds: s.grounds.filter((g) => near(g.x, g.y, Math.hypot(g.rw, g.rh) + 4)),
      passes: s.passes.filter((p) => near(p.x, p.y, p.r * 2.2)),
      roads: roads.map((r) => ({ ...r, segs: segsNear(r.p, x0, y0, x1, y1, 8) })).filter((r) => r.segs.length),
      spires: spires.filter((p) => near(p.x, p.y, p.r * 1.2)),
      ridge: ((segs) => (segs.length || (s.flipped ? y0 < s.southRidge.edgeFrom : y1 > s.southRidge.edgeFrom) ? segs : null))(
        segsNear(ridge, x0, y0, x1, y1, s.southRidge.reach),
      ),
      rim: !outline
        ? 0
        : segsNear(outline, x0, y0, x1, y1, OUTLINE_RIM.reach + 2).length
          ? 2
          : insidePoly(s.outline!, (x0 + x1) / 2, (y0 + y1) / 2)
            ? 0
            : 1,
    };
    buckets.set(key, b);
    return b;
  };
  return { s, nc, spires, stones, ridge, outline, bucket };
}

// ── the 4 m cells ───────────────────────────────────────────────────────────────────────────

export const SKETCH_CELL = 4;

/** The coast's y at x (the sea lies north of it). */
function coastY(s: RegionSketch, x: number): number {
  const c = s.coast;
  for (let k = 1; k < c.length; k++) {
    const [x0, y0] = c[k - 1]!;
    const [x1, y1] = c[k]!;
    if (x <= x1) return lerp(y0, y1, (x - x0) / (x1 - x0));
  }
  return c[c.length - 1]![1];
}

export interface Cell {
  h: number;
  kind: RegionKind;
  /** Water level where wet, else -999. */
  water: number;
}

/**
 * The sketch at a cell centre (x, y), before ground cover: height, sea / land / spire / lake /
 * river, and the water level. `b` must be the bucket of a chunk whose margin holds (x, y).
 */
export function baseCell(C: CompiledSketch, b: Bucket, x: number, y: number): Cell {
  const s = C.s;
  const u = s.unit ?? 1;
  const n1 = fbm(x / (180 * u), y / (180 * u), 1);
  const n2 = fbm(x / (45 * u), y / (45 * u), 2, 3);
  const yc = coastY(s, x) + (n1 - 0.5) * (50 * u);
  let land = s.flipped ? yc - y : y - yc;
  // The mainland outside the outline (before the islands: they stay as they are).
  const mainland = land > 0;
  let hh =
    land < 0
      ? Math.max(-30 * u, land * 0.08)
      : Math.min(26 * u, 1.2 * u + land * 0.05) + (n1 - 0.5) * (10 * u) * Math.min(1, land / (120 * u));
  let water = -999;
  for (const isl of b.islands) {
    const d = Math.hypot(x - isl.x, y - isl.y) / (isl.r * (0.85 + 0.3 * n2));
    if (d < 1) {
      land = 1;
      hh = Math.max(hh, isl.h * Math.min(1, (1 - d) * 3) ** 0.7);
    }
  }
  // Mountains.
  let m = -Infinity;
  for (const p of b.peaks) {
    const d = Math.hypot(x - p.x, y - p.y) / p.r;
    if (d < 1) m = Math.max(m, p.h * (1 - d) ** 1.6);
  }
  if (b.ridge) {
    const R = s.southRidge;
    const rd = b.ridge.length ? polyNear(C.ridge, x, y, b.ridge).d : Infinity;
    if (rd < R.reach) m = Math.max(m, R.h * (1 - rd / R.reach) ** 1.5);
    const past = s.flipped ? R.edgeFrom - y : y - R.edgeFrom;
    if (past > 0) m = Math.max(m, R.h * Math.min(1, past / (250 * u)));
  }
  if (b.rim && mainland) {
    const R = OUTLINE_RIM;
    const out = b.rim === 1 ? R.reach : insidePoly(s.outline!, x, y) ? 0 : polyNear(C.outline!, x, y).d;
    if (out > 0) m = Math.max(m, R.edge + (R.top - R.edge) * smooth(out / R.reach));
  }
  for (const ps of b.passes) {
    const d = Math.hypot(x - ps.x, y - ps.y);
    if (d < ps.r * 2.2) m = lerp(Math.min(m, 55 * u), m, smooth(d / (ps.r * 2.2)));
  }
  if (m > 0 && land > 0) {
    const rough = 0.75 + 0.5 * n2;
    hh = Math.max(hh, m * rough + (n1 - 0.5) * m * 0.25);
  }
  if (s.maxHeight !== undefined) hh = Math.min(hh, s.maxHeight);
  // Flats.
  for (const f of b.flats) {
    const d = Math.hypot(x - f.x, y - f.y) / f.r;
    if (d < 1.4) hh = lerp(f.h + (n2 - 0.5) * (f.noise ?? 2 * u), hh, smooth((d - 0.8) / 0.6));
  }
  // Spires (the tiles' own tops are flattened in generate.ts).
  let spire = false;
  for (const sp of b.spires) {
    const d = Math.hypot(x - sp.x, y - sp.y);
    if (d < sp.r * (0.85 + 0.3 * n2)) {
      hh = sp.top - (d / sp.r) * (6 * u);
      spire = true;
    }
  }
  let k: RegionKind = land < 0 ? RK.SEA : RK.GRASS;
  if (spire) k = RK.SPIRE;
  if (k === RK.SEA) water = 0;
  // Lakes.
  for (const l of b.lakes) {
    const d = Math.hypot((x - l.x) / l.rx, (y - l.y) / l.ry) * (0.9 + 0.2 * n2);
    if (d < 1) {
      hh = Math.min(hh, l.h - 0.4 * u);
      water = l.h;
      k = l.marsh && n2 > 0.55 ? RK.MARSH : RK.LAKE;
    } else if (d < 1.6) hh = lerp(l.h + 0.6 * u, hh, smooth((d - 1) / 0.6));
  }
  // Rivers carve their valleys.
  for (const { r, p, segs } of b.rivers) {
    const near = polyNear(p, x, y, segs);
    const d = near.d;
    if (d > 160 * u) continue;
    const seg = near.t * (r.pts.length - 1);
    const a = Math.min(r.pts.length - 2, Math.floor(seg));
    const f = seg - a;
    const bed = lerp(r.bed[a]!, r.bed[a + 1]!, f);
    const w = lerp(r.w[a]!, r.w[a + 1]!, f) * (0.85 + 0.3 * n2);
    if (d < w / 2) {
      if (bed > -0.5) {
        hh = bed - 0.6 * u;
        water = bed;
        if (k !== RK.SPIRE) k = RK.RIVER;
      }
    } else {
      const bank = 40 * u + Math.max(0, hh - bed) * 0.8;
      if (d < w / 2 + bank) hh = Math.min(hh, lerp(bed + 1.2 * u, hh, smooth((d - w / 2) / bank)));
    }
  }
  return { h: hh, kind: k, water };
}

/**
 * Ground cover of a grass cell, by height, slope and the cover zones (region-b.mjs's second pass).
 * `slope` is the cell's steepest neighbour difference over 2 cells; `seaNorth` whether the cell
 * three to the north is sea.
 */
export function coverOf(
  C: CompiledSketch,
  b: Bucket,
  x: number,
  y: number,
  hh: number,
  slope: number,
  seaNorth: boolean,
): RegionKind {
  const u = C.s.unit ?? 1;
  const n3 = fbm(x / (60 * u), y / (60 * u), 5, 3);
  const yc = coastY(C.s, x);
  let k: RegionKind = RK.GRASS;
  const inland = C.s.flipped ? yc - y : y - yc;
  if (hh < 2.2 * u && inland < 60 * u) k = RK.SAND;
  else if (hh > 2 * u && (C.s.flipped ? y > C.s.size - 330 * u : y < 330 * u) && seaNorth && slope > 0.5) k = RK.CLIFF;
  else if (slope > 1.0) k = RK.CLIFF;
  else if (hh > 300 * u) k = RK.ROCK;
  else if (hh > 110 * u) k = n3 > 0.42 ? RK.PINE : slope > 0.6 ? RK.ROCK : RK.GRASS;
  else if (n3 > 0.6) k = hh > 60 * u ? RK.PINE : RK.FOREST;
  if (b.cover.length) {
    const wxn = fbm(x / (140 * u), y / (140 * u), 8, 3) - 0.5;
    const wyn = fbm(x / (140 * u), y / (140 * u), 9, 3) - 0.5;
    for (const [ck, cx, cy, r] of b.cover) {
      const d = Math.hypot(x + wxn * r * 0.9 - cx, y + wyn * r * 0.9 - cy) / (r * (0.8 + 0.4 * n3));
      if (d < 1 && slope < 1.0 && hh > 2.2 * u) {
        if (ck === RK.FARM && slope > 0.12) continue;
        if (ck === RK.REDROCK) k = slope > 0.5 ? RK.REDROCK : k === RK.CLIFF ? RK.REDROCK : k;
        else k = ck;
      }
    }
  }
  return k;
}

/** The town / camp / ruin ground at a point, if any (the sketch's `town` grounds). */
export function groundKind(b: Bucket, x: number, y: number): RegionKind | null {
  let out: RegionKind | null = null;
  for (const g of b.grounds) {
    if (g.round) {
      if (Math.hypot((x - g.x) / g.rw, (y - g.y) / g.rh) > 1) continue;
    } else if (Math.abs(x - g.x) > g.rw || Math.abs(y - g.y) > g.rh) continue;
    out = g.cover === 'camp' ? RK.CAMP : g.cover === 'ruin' ? RK.RUIN : RK.TOWN;
  }
  return out;
}
